import axios from 'axios';

const TOKEN_KEY = 'auth_token';

const api = axios.create({ baseURL: '/api' });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.config?.url !== '/auth/login') {
      localStorage.removeItem(TOKEN_KEY);
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Auth
export const login          = (username, password) => api.post('/auth/login', { username, password }).then(r => r.data);
export const getCurrentUser = ()                    => api.get('/auth/me').then(r => r.data);
export const getAuthToken   = () => localStorage.getItem(TOKEN_KEY);
export const setAuthToken   = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearAuthToken = () => localStorage.removeItem(TOKEN_KEY);

// Devices
export const getDevices          = ()          => api.get('/devices').then(r => r.data);
export const getDevice           = (id)        => api.get(`/devices/${id}`).then(r => r.data);
export const createDevice        = (data)      => api.post('/devices', data).then(r => r.data);
export const updateDevice        = (id, data)  => api.put(`/devices/${id}`, data).then(r => r.data);
export const deleteDevice        = (id)        => api.delete(`/devices/${id}`).then(r => r.data);

// Alert Settings
export const getAlertSetting     = (deviceId)  => api.get(`/devices/${deviceId}/alert-settings`).then(r => r.data);
export const saveAlertSetting    = (deviceId, data) => api.put(`/devices/${deviceId}/alert-settings`, data).then(r => r.data);

// WhatsApp Recipients
export const getWhatsappRecs     = (deviceId)  => api.get(`/devices/${deviceId}/whatsapp-recipients`).then(r => r.data);
export const createWhatsappRec   = (deviceId, data) => api.post(`/devices/${deviceId}/whatsapp-recipients`, data).then(r => r.data);
export const updateWhatsappRec   = (deviceId, id, data) => api.put(`/devices/${deviceId}/whatsapp-recipients/${id}`, data).then(r => r.data);
export const deleteWhatsappRec   = (deviceId, id) => api.delete(`/devices/${deviceId}/whatsapp-recipients/${id}`).then(r => r.data);
export const bulkSaveWhatsappRecs = (deviceId, numbers) => api.put(`/devices/${deviceId}/whatsapp-recipients/bulk`, { numbers }).then(r => r.data);
export const testAllWhatsapp      = (deviceId, data)    => api.post(`/devices/${deviceId}/whatsapp-recipients/test-all`, data).then(r => r.data);

// Email Recipients
export const getEmailRecs        = (deviceId)  => api.get(`/devices/${deviceId}/email-recipients`).then(r => r.data);
export const createEmailRec      = (deviceId, data) => api.post(`/devices/${deviceId}/email-recipients`, data).then(r => r.data);
export const updateEmailRec      = (deviceId, id, data) => api.put(`/devices/${deviceId}/email-recipients/${id}`, data).then(r => r.data);
export const deleteEmailRec      = (deviceId, id) => api.delete(`/devices/${deviceId}/email-recipients/${id}`).then(r => r.data);

// Alert Rules
export const getAlertRules       = (deviceId)  => api.get(`/devices/${deviceId}/alert-rules`).then(r => r.data);
export const createAlertRule     = (deviceId, data) => api.post(`/devices/${deviceId}/alert-rules`, data).then(r => r.data);
export const updateAlertRule     = (deviceId, id, data) => api.put(`/devices/${deviceId}/alert-rules/${id}`, data).then(r => r.data);
export const deleteAlertRule     = (deviceId, id) => api.delete(`/devices/${deviceId}/alert-rules/${id}`).then(r => r.data);

// Alert Events
export const getAlertEvents      = (deviceId, params) =>
  api.get(deviceId ? `/devices/${deviceId}/alert-events` : '/alert-events', { params }).then(r => r.data);

// Jobs (Delivery Status)
export const getAlertJobs        = (deviceId, params) =>
  api.get(deviceId ? `/devices/${deviceId}/alert-jobs` : '/alert-jobs', { params }).then(r => r.data);

// Ingest (for testing)
export const ingestData          = (deviceId, payload) => api.post(`/ingest/${deviceId}`, payload).then(r => r.data);

// Latest sensor readings
export const getDeviceReading    = (deviceId)       => api.get(`/devices/${deviceId}/readings`).then(r => r.data);

// WhatsApp config (template mode detection)
export const getWhatsappConfig   = ()                    => api.get('/config/whatsapp').then(r => r.data);

// Test alerts (sends immediately, returns result)
export const testWhatsappAlert   = (deviceId, id, data) => api.post(`/devices/${deviceId}/whatsapp-recipients/${id}/test`, data).then(r => r.data);
export const testEmailAlert      = (deviceId, id, data) => api.post(`/devices/${deviceId}/email-recipients/${id}/test`,    data).then(r => r.data);

// External Data Sources
export const getDataSources      = (deviceId)       => api.get(`/devices/${deviceId}/data-sources`).then(r => r.data);
export const createDataSource    = (deviceId, data) => api.post(`/devices/${deviceId}/data-sources`, data).then(r => r.data);
export const updateDataSource    = (deviceId, id, data) => api.put(`/devices/${deviceId}/data-sources/${id}`, data).then(r => r.data);
export const deleteDataSource    = (deviceId, id)   => api.delete(`/devices/${deviceId}/data-sources/${id}`).then(r => r.data);
export const testDataSource      = (deviceId, id)   => api.post(`/devices/${deviceId}/data-sources/${id}/test`).then(r => r.data);
export const testDataSourceMapping = (deviceId, data) => api.post(`/devices/${deviceId}/data-sources/test-mapping`, data).then(r => r.data);

// Daily Report
export const getDailyReport      = (deviceId)       => api.get(`/devices/${deviceId}/daily-report`).then(r => r.data);
export const saveDailyReport     = (deviceId, data) => api.put(`/devices/${deviceId}/daily-report`, data).then(r => r.data);
export const previewDailyReport  = (deviceId, data) => api.post(`/devices/${deviceId}/daily-report/preview`, data).then(r => r.data);
export const getDailyReportVars  = ()               => api.get('/daily-report/variables').then(r => r.data);
export const getDefaultTemplate  = ()               => api.get('/daily-report/default-template').then(r => r.data);

// Settings templates
export const getSettingsTemplates      = ()          => api.get('/settings/templates').then(r => r.data);
export const getSettingsTemplate       = (id)        => api.get(`/settings/templates/${id}`).then(r => r.data);
export const validateSettingsTemplateSource = (data) => api.post('/settings/templates/validate-source', data).then(r => r.data);
export const createSettingsTemplate    = (data)      => api.post('/settings/templates', data).then(r => r.data);
export const updateSettingsTemplate    = (id, data)  => api.put(`/settings/templates/${id}`, data).then(r => r.data);
export const deleteSettingsTemplate    = (id)        => api.delete(`/settings/templates/${id}`).then(r => r.data);
export const testSendSettingsTemplate  = (id, data)  => api.post(`/settings/templates/${id}/test-send`, data).then(r => r.data);

// Device Mappings (Devices module)
export const getDeviceMappings      = ()          => api.get('/device-mappings').then(r => r.data);
export const getDeviceMapping       = (id)        => api.get(`/device-mappings/${id}`).then(r => r.data);
export const createDeviceMapping    = (data)      => api.post('/device-mappings', data).then(r => r.data);
export const updateDeviceMapping    = (id, data)  => api.put(`/device-mappings/${id}`, data).then(r => r.data);
export const deleteDeviceMapping    = (id)        => api.delete(`/device-mappings/${id}`).then(r => r.data);
export const previewDeviceMapping   = (id, data)  => api.post(`/device-mappings/${id}/preview`, data).then(r => r.data);
export const testDeviceMapping      = (id, data)  => api.post(`/device-mappings/${id}/test`, data).then(r => r.data);
export const bulkImportDeviceMappings = (data)    => api.post('/device-mappings/bulk-import', data).then(r => r.data);
